import { useContext, useEffect, useState } from "react";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { PageContainer } from "../../components/page";
import NotificationHistory from "./notification-history";
import { useNavigate } from "react-router";
import { runAuthHandler } from "../util";
import { observer } from "mobx-react";
import RadioAndButtonGroupContainer from "./RadioAndButtonGroupContainer";
import ScheduledNotificationsContainer from "../scheduled-notifications/ScheduledNotificationsContainer";
import { getFlashHistory, getScheduledNotifications } from "../../api/new-api";
import { useColors } from "../../provider/colors-provider";

const PAGE_TITLE = "Mobile Notifications";

const TABS = [
  {
    name: "Single User",
    value: "single-user",
    description: "Send a notification to a single user",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>
    ),
  },
  {
    name: "Whole Venue",
    value: "whole-venue",
    description: "Send a notification to all venue users",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
        />
      </svg>
    ),
  },
  {
    name: "By User Tier",
    value: "custom-filter",
    description: "Send a notification to users based on their tier level",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
        />
      </svg>
    ),
  },
  {
    name: "CSV Import",
    value: "csv-import",
    description: "Send a notification to users using CSV file",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
        />
      </svg>
    ),
  },
];

const fetchScheduledNotificationHistory = async ({
  setData,
  setLoading,
  setError,
}: {
  setData: any;
  setLoading: any;
  setError: any;
}) => {
  setError("");
  setLoading(true);
  try {
    const res = await getScheduledNotifications();
    if (res.data) {
      setData(res.data);
    } else {
      setError("Error getting scheduled notifications");
    }
  } catch (error) {
    setError("Error getting scheduled notifications");
  } finally {
    setLoading(false);
  }
};

const fetchNotificationHistory = async ({
  setData,
  setLoading,
  setError,
}: {
  setData: any;
  setLoading: any;
  setError: any;
}) => {
  setError("");
  setLoading(true);
  try {
    const res = await getFlashHistory();
    if (res.status === 200) {
      setData(res.data.history);
    } else {
      setError("Error getting notifications history");
    }
  } catch (error) {
    setError("Error getting notifications history");
  } finally {
    setLoading(false);
  }
};

export const SCHEDULE_NOTIFICATIONS = {
  NOW: "NOW",
  FUTURE: "FUTURE",
};

const PlayerCommunicationsPage = () => {
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;
  const navigate = useNavigate();
  const [activeHistoryView, setActiveHistoryView] = useState<string | null>(
    "notification"
  );
  const [dynamicTabs, setDynamicTabs] = useState(TABS);
  const [scheduledNotifications, setScheduledNotifications] =
    useState<any>(null);
  const [curentTabSelected, setCurrentTabSelected] = useState<any>();

  const [date, setDate] = useState(new Date());

  const [notificationLoading, setNotificationLoading] = useState(true);
  const [notificationError, setNotificationError] = useState<any>("");
  const [notificationData, setNotificationData] = useState<any>([]);

  const [schedNotificationLoading, setSchedNotificationLoading] =
    useState(true);
  const [schedNotificationError, setSchedNotificationError] = useState<any>("");
  const [schedNotificationData, setSchedNotificationData] = useState<any>([]);

  const refetch = () => {
    fetchScheduledNotificationHistory({
      setData: setSchedNotificationData,
      setLoading: setSchedNotificationLoading,
      setError: setSchedNotificationError,
    });

    fetchNotificationHistory({
      setData: setNotificationData,
      setLoading: setNotificationLoading,
      setError: setNotificationError,
    });
  };

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
    refetch();
  }, [selectedVenue]);

  useEffect(() => {
    setDynamicTabs(TABS);
    setCurrentTabSelected(null);
  }, [scheduledNotifications]);

  const { primary } = useColors();
  return (
    <PageContainer>
      <BreadCrumbs
        routes={[
          { path: "/home", breadcrumbName: "Home" },
          {
            path: "/player-communications",
            breadcrumbName: "Player Messaging",
          },
        ]}
      />
      <div className="text-3xl mb-6 text-center font-bold">{PAGE_TITLE}</div>

      <RadioAndButtonGroupContainer
        dynamicTabs={dynamicTabs}
        setCurrentTabSelected={setCurrentTabSelected}
        scheduledNotifications={scheduledNotifications}
        setScheduledNotifications={setScheduledNotifications}
        curentTabSelected={curentTabSelected}
        date={date}
        setDate={setDate}
        refetch={refetch}
      />
      <div className="text-center text-3xl mb-2 font-bold">
        Notifications History
      </div>

      <div className="flex justify-center gap-4 mb-6">
        <button
          style={{
            border:
              activeHistoryView === "notification"
                ? `2px solid ${primary}`
                : "",
          }}
          className="hover:cursor-pointer hover:opacity-70 hover:shadow-2xl transition-all max-w-sm p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700"
          onClick={() =>
            setActiveHistoryView(
              activeHistoryView === "notification" ? null : "notification"
            )
          }
        >
          Notification History
        </button>
        <button
          style={{
            border:
              activeHistoryView === "scheduled" ? `2px solid ${primary}` : "",
          }}
          className="hover:cursor-pointer hover:opacity-70 hover:shadow-2xl transition-all max-w-sm p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700"
          onClick={() =>
            setActiveHistoryView(
              activeHistoryView === "scheduled" ? null : "scheduled"
            )
          }
        >
          Scheduled Notifications
        </button>
      </div>

      <div className="min-h-[200px] justify-center flex">
        {activeHistoryView === "notification" && (
          <NotificationHistory
            data={notificationData}
            loading={notificationLoading}
            error={notificationError}
          />
        )}
        {activeHistoryView === "scheduled" && (
          <ScheduledNotificationsContainer
            data={schedNotificationData}
            loading={schedNotificationLoading}
            error={schedNotificationError}
            direction="column"
            refetch={refetch}
          />
        )}
        {!activeHistoryView && (
          <div className="text-center text-gray-500 py-8">
            Select a history type to view
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default observer(PlayerCommunicationsPage);
